import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Integration, { SALESFORCE_OBJECT_TYPES, SalesforceObjectType } from "@/model/interface/Integration";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  useUpdateFieldMappingMutation,
  useGetDefaultFieldMappingsQuery,
  useDescribeSalesforceObjectQuery,
  useUpdateIntegrationSettingsMutation,
} from "@/store/rest_api";
import toast from "react-hot-toast";

interface Props {
  integration: Integration;
}

const PLATFORM_FIELDS = [
  { value: "firstName", label: "First Name" },
  { value: "lastName", label: "Last Name" },
  { value: "company", label: "Company" },
  { value: "position", label: "Position" },
  { value: "address", label: "Address" },
  { value: "phones.0.phoneNumber", label: "Phone (Primary)" },
  { value: "phones.1.phoneNumber", label: "Phone (Secondary)" },
  { value: "phones.2.phoneNumber", label: "Phone (Tertiary)" },
  { value: "emails.0.email", label: "Email (Primary)" },
  { value: "emails.1.email", label: "Email (Secondary)" },
  { value: "link", label: "Website" },
];

function SalesforceFieldSelect({
  objectType,
  value,
  onChange,
  isActive,
}: {
  objectType: SalesforceObjectType;
  value: string;
  onChange: (value: string) => void;
  isActive: boolean;
}) {
  const { data: sfFields } = useDescribeSalesforceObjectQuery(objectType, { skip: !isActive });

  if (!sfFields || sfFields.length === 0) {
    return (
      <Input
        value={value}
        placeholder="e.g. Phone, MobilePhone"
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue placeholder="Select SF field" />
      </SelectTrigger>
      <SelectContent className="max-h-60">
        {sfFields.map((field) => (
          <SelectItem key={field.name} value={field.name}>
            {field.label} ({field.name})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function PhoneSearchFields({
  objectType,
  integration,
  mapping,
  onUpdate,
  isActive,
}: {
  objectType: SalesforceObjectType;
  integration: Integration;
  mapping: Record<string, string>;
  onUpdate: ReturnType<typeof useUpdateIntegrationSettingsMutation>[0];
  isActive: boolean;
}) {
  const { data: sfFields } = useDescribeSalesforceObjectQuery(objectType, { skip: !isActive });
  const settingKey = `phoneSearchFields_${objectType}`;
  const configuredFields = (integration.settings?.[settingKey] as string[]) || null;

  const phoneRelatedSfFields = Object.keys(mapping).filter(
    (sfField) => mapping[sfField]?.startsWith("phones.")
  );

  const allPhoneFields = sfFields
    ? sfFields.filter((field) => field.type === "phone" || field.name.toLowerCase().includes("phone"))
    : [];

  const displayFields = allPhoneFields.length > 0
    ? allPhoneFields.map((field) => field.name)
    : phoneRelatedSfFields;

  if (displayFields.length === 0) {
    return null;
  }

  const activeFields = configuredFields || phoneRelatedSfFields;

  const toggleField = async (fieldName: string, checked: boolean) => {
    const updated = checked
      ? [...activeFields, fieldName]
      : activeFields.filter((existing) => existing !== fieldName);
    try {
      await onUpdate({ id: integration.id, settings: { [settingKey]: updated } }).unwrap();
    } catch {
      // silently fail
    }
  };

  return (
    <div className="mt-4 pt-4 border-t">
      <Label className="text-xs text-muted-foreground font-medium">Phone Search Fields</Label>
      <p className="text-xs text-muted-foreground mb-2">
        Which Salesforce fields to search when looking up contacts by phone number
      </p>
      <div className="space-y-2">
        {displayFields.map((fieldName) => {
          const sfField = allPhoneFields.find((field) => field.name === fieldName);
          const label = sfField ? `${sfField.label} (${sfField.name})` : fieldName;
          return (
            <div key={fieldName} className="flex items-center gap-2">
              <Checkbox
                id={`phone-${objectType}-${fieldName}`}
                checked={activeFields.includes(fieldName)}
                onCheckedChange={(checked) => toggleField(fieldName, checked === true)}
              />
              <Label htmlFor={`phone-${objectType}-${fieldName}`} className="text-sm font-normal">
                {label}
              </Label>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SalesforceFieldMapping({ integration }: Props) {
  const { data: defaults } = useGetDefaultFieldMappingsQuery(integration.id);
  const [updateMapping] = useUpdateFieldMappingMutation();
  const [updateSettings] = useUpdateIntegrationSettingsMutation();
  const [localMapping, setLocalMapping] = useState<Record<string, Record<string, string>>>({});
  const [dirty, setDirty] = useState(false);
  const isActive = integration.status?.toLowerCase() === "active";

  useEffect(() => {
    if (defaults) {
      const merged: Record<string, Record<string, string>> = {};
      for (const objectType of SALESFORCE_OBJECT_TYPES) {
        const defaultFields = defaults[objectType] || {};
        const customFields = (integration.fieldMapping?.[objectType] as Record<string, string>) || {};
        merged[objectType] = { ...defaultFields, ...customFields };
      }
      setLocalMapping(merged);
    }
  }, [defaults, integration.fieldMapping]);

  const updateField = (objectType: string, salesforceField: string, platformField: string) => {
    setLocalMapping((prev) => ({
      ...prev,
      [objectType]: {
        ...prev[objectType],
        [salesforceField]: platformField,
      },
    }));
    setDirty(true);
  };

  const renameSalesforceField = (objectType: string, fieldIndex: number, newFieldName: string) => {
    setLocalMapping((prev) => {
      const entries = Object.entries(prev[objectType] || {});
      const updated: Record<string, string> = {};
      entries.forEach(([existingField, platformField], entryIndex) => {
        updated[entryIndex === fieldIndex ? newFieldName : existingField] = platformField;
      });
      return { ...prev, [objectType]: updated };
    });
    setDirty(true);
  };

  const addField = (objectType: string) => {
    setLocalMapping((prev) => ({
      ...prev,
      [objectType]: {
        ...prev[objectType],
        "": "",
      },
    }));
    setDirty(true);
  };

  const removeField = (objectType: string, salesforceField: string) => {
    setLocalMapping((prev) => {
      const updated = { ...prev[objectType] };
      delete updated[salesforceField];
      return { ...prev, [objectType]: updated };
    });
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      await updateMapping({ id: integration.id, fieldMapping: localMapping }).unwrap();
      toast.success("Field mapping saved");
      setDirty(false);
    } catch {
      toast.error("Failed to save field mapping");
    }
  };

  return (
    <div className="space-y-6">
      {SALESFORCE_OBJECT_TYPES.map((objectType) => {
        const fields = localMapping[objectType] || {};
        return (
          <Card key={objectType}>
            <CardHeader>
              <CardTitle className="capitalize">{objectType.toLowerCase()} Mapping</CardTitle>
              <CardDescription>
                Map Salesforce {objectType.toLowerCase()} fields to platform contact fields
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center text-xs text-muted-foreground font-medium">
                <span>Salesforce Field</span>
                <span>Platform Field</span>
                <span className="w-8" />
              </div>

              {Object.entries(fields).map(([salesforceField, platformField], fieldIndex) => (
                <div key={`${objectType}-${fieldIndex}`} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <SalesforceFieldSelect
                    objectType={objectType}
                    value={salesforceField}
                    onChange={(newName) => renameSalesforceField(objectType, fieldIndex, newName)}
                    isActive={isActive}
                  />
                  <Select
                    value={platformField}
                    onValueChange={(newValue) => updateField(objectType, salesforceField, newValue)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select platform field" />
                    </SelectTrigger>
                    <SelectContent>
                      {PLATFORM_FIELDS.map((field) => (
                        <SelectItem key={field.value} value={field.value}>
                          {field.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => removeField(objectType, salesforceField)}
                  >
                    &times;
                  </Button>
                </div>
              ))}

              <Button variant="outline" size="sm" onClick={() => addField(objectType)}>
                Add Field
              </Button>

              <PhoneSearchFields
                objectType={objectType}
                integration={integration}
                mapping={fields}
                onUpdate={updateSettings}
                isActive={isActive}
              />
            </CardContent>
          </Card>
        );
      })}

      {dirty && (
        <div className="flex justify-end">
          <Button onClick={handleSave}>Save Mappings</Button>
        </div>
      )}
    </div>
  );
}
